import { useEffect } from "react";
import { useLocale } from "../../i18n/LocaleContext";
import { buildLocalizedPath } from "../../lib/siteRouting";

const facts = [
  ["legalName", "주식회사 천도그룹"],
  ["brandName", "QIANDU / 千渡"],
  ["businessNumber", "328-87-03814"],
  ["corporateNumber", "120111-0149603"],
  ["openingDate", "2025-11-12"],
  ["registeredAddress", "54, Bukhang-ro 193beon-gil, Seo-gu, Incheon 22856, Republic of Korea"],
  ["registeredScope", "Wholesale and retail trade; cosmetics export"],
  ["issuingOffice", "Seo Incheon Tax Office"],
] as const;

export function CompanyVerificationPage() {
  const { locale } = useLocale();
  const copy = {
    en: {
      back: "Back to home",
      eyebrow: "Company verification",
      title: "A clearer legal identity for trusted cross-border cooperation.",
      intro: "QIANDU is the international operating brand of 주식회사 천도그룹, a Korean corporation legally registered in Incheon, Republic of Korea.",
      verified: "Registration facts",
      labels: {
        legalName: "Korean legal name", brandName: "Operating brand", businessNumber: "Business registration number",
        corporateNumber: "Corporate registration number", openingDate: "Business commencement date",
        registeredAddress: "Registered head-office address", registeredScope: "Registered business scope", issuingOffice: "Issuing tax office",
      },
      evidence: "Evidence status",
      evidenceBody: "The registration details shown here are transcribed from a company-provided Korean Business Registration Certificate issued on 14 November 2025. Public business-information records provide supporting entity matches. Official current status should be confirmed through the Korean National Tax Service when required for a transaction.",
      boundaries: "Scope and claim boundaries",
      boundaryItems: [
        "The Incheon address is the registered business and head-office address. It is not described here as a public shop or warehouse.",
        "Business registration does not itself prove brand authorization, inventory ownership, or regulatory approval in an importing market.",
        "Brand relationships, product lines, territories, document availability, and authorization periods must be confirmed for each project.",
      ],
      updated: "Entity facts last reviewed: 31 October 2026",
    },
    zh: {
      back: "返回首页",
      eyebrow: "企业核验",
      title: "以清晰的韩国法人身份建立跨境合作信任。",
      intro: "QIANDU／千渡是韩国法人 주식회사 천도그룹 的国际运营品牌，该法人依法登记于韩国仁川广域市。",
      verified: "登记事实",
      labels: {
        legalName: "韩国法定名称", brandName: "运营品牌", businessNumber: "营业登记号",
        corporateNumber: "法人登记号", openingDate: "开业日期", registeredAddress: "法定总部注册地址",
        registeredScope: "登记经营范围", issuingOffice: "签发税务机关",
      },
      evidence: "证据状态",
      evidenceBody: "本页登记信息依据公司提供、签发于2025年11月14日的韩国《营业者登记证》录入，并有公开企业信息记录提供辅助实体匹配。具体交易需要官方现行状态时，应通过韩国国税厅系统再次核验。",
      boundaries: "范围与声明边界",
      boundaryItems: [
        "仁川地址是法定营业场所及本店注册地址；本页不将其描述为公开门店或仓库。",
        "企业登记本身不证明品牌授权、库存所有权，也不代表已满足进口市场的产品合规要求。",
        "品牌关系、产品线、授权区域、文件供应能力及有效期须按具体项目分别确认。",
      ],
      updated: "企业信息最近复核：2026年10月31日",
    },
    ko: {
      back: "홈으로 돌아가기",
      eyebrow: "기업 정보 확인",
      title: "명확한 한국 법인 정보로 신뢰할 수 있는 글로벌 협력을 지원합니다.",
      intro: "QIANDU는 대한민국 인천광역시에 법인 등록된 주식회사 천도그룹의 글로벌 운영 브랜드입니다.",
      verified: "등록 정보",
      labels: {
        legalName: "법인명", brandName: "운영 브랜드", businessNumber: "사업자등록번호",
        corporateNumber: "법인등록번호", openingDate: "개업일", registeredAddress: "본점 등록 주소",
        registeredScope: "등록 사업 범위", issuingOffice: "발급 세무서",
      },
      evidence: "증빙 상태",
      evidenceBody: "본 페이지의 등록 정보는 회사가 제공한 2025년 11월 14일 발급 사업자등록증을 기준으로 작성되었으며 공개 사업자 정보가 동일 법인 식별을 보조합니다. 거래 시 공식 현재 상태가 필요한 경우 국세청에서 다시 확인해야 합니다.",
      boundaries: "범위 및 표시 기준",
      boundaryItems: [
        "인천 주소는 법정 사업장 및 본점 등록 주소이며 공개 매장 또는 창고로 표시하지 않습니다.",
        "사업자등록만으로 브랜드 권한, 재고 소유권 또는 수입국의 제품 규제 승인을 증명하지 않습니다.",
        "브랜드 관계, 제품군, 권역, 제공 문서 및 권한 기간은 프로젝트별로 확인합니다.",
      ],
      updated: "기업 정보 최종 검토일: 2026년 10월 31일",
    },
    vi: {
      back: "Quay lại trang chủ",
      eyebrow: "Xác minh doanh nghiệp",
      title: "Thông tin pháp nhân rõ ràng cho hợp tác xuyên biên giới đáng tin cậy.",
      intro: "QIANDU là thương hiệu vận hành quốc tế của 주식회사 천도그룹, pháp nhân Hàn Quốc đăng ký hợp pháp tại Incheon.",
      verified: "Thông tin đăng ký",
      labels: {
        legalName: "Tên pháp lý tại Hàn Quốc", brandName: "Thương hiệu vận hành", businessNumber: "Mã đăng ký kinh doanh",
        corporateNumber: "Mã đăng ký pháp nhân", openingDate: "Ngày bắt đầu hoạt động", registeredAddress: "Địa chỉ trụ sở đăng ký",
        registeredScope: "Phạm vi kinh doanh đăng ký", issuingOffice: "Cơ quan thuế cấp",
      },
      evidence: "Tình trạng bằng chứng",
      evidenceBody: "Thông tin trên trang được ghi từ Giấy chứng nhận đăng ký kinh doanh Hàn Quốc do công ty cung cấp, cấp ngày 14/11/2025, và được đối chiếu hỗ trợ bằng hồ sơ doanh nghiệp công khai. Khi giao dịch cần xác nhận tình trạng hiện hành qua Cơ quan Thuế Quốc gia Hàn Quốc.",
      boundaries: "Phạm vi và giới hạn tuyên bố",
      boundaryItems: [
        "Địa chỉ Incheon là địa chỉ kinh doanh và trụ sở đăng ký; trang này không mô tả đây là cửa hàng công cộng hoặc kho hàng.",
        "Đăng ký doanh nghiệp không tự chứng minh quyền phân phối thương hiệu, quyền sở hữu tồn kho hoặc phê duyệt tại thị trường nhập khẩu.",
        "Quan hệ thương hiệu, dòng sản phẩm, lãnh thổ, tài liệu và thời hạn quyền phải được xác nhận theo từng dự án.",
      ],
      updated: "Thông tin pháp nhân được rà soát gần nhất: 31/10/2026",
    },
  }[locale];

  useEffect(() => {
    document.title = `${copy.eyebrow} | QIANDU`;
    const description = copy.intro;
    document.head.querySelector('meta[name="description"]')?.setAttribute("content", description);
    document.head.querySelector('meta[property="og:title"]')?.setAttribute("content", `${copy.eyebrow} | QIANDU`);
    document.head.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
    document.head.querySelector('link[rel="canonical"]')?.setAttribute("href", `https://www.qiandu.group${buildLocalizedPath(locale, "/company/verification")}`);
  }, [copy.eyebrow, copy.intro, locale]);

  return (
    <main className="px-5 pb-20 pt-24 sm:px-8 lg:px-12 lg:pb-24 lg:pt-32">
      <div className="mx-auto max-w-6xl">
        <a href={buildLocalizedPath(locale, "/")} className="inline-flex rounded-full border border-[#dce8ee] bg-white/80 px-5 py-3 text-[11px] uppercase tracking-[0.22em] text-soft/64 shadow-sm">
          {copy.back}
        </a>
        <section className="mt-10 rounded-[2.8rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.94),rgba(238,246,249,0.94))] p-7 shadow-[0_18px_40px_rgba(88,124,140,0.08)] sm:p-10 lg:p-14">
          <div className="text-[0.68rem] uppercase tracking-[0.5em] text-aurora/72">{copy.eyebrow}</div>
          <h1 className="mt-5 max-w-4xl font-display text-[2.3rem] leading-[1.06] text-[#1f2b31] sm:text-[3.4rem]">{copy.title}</h1>
          <p className="mt-6 max-w-3xl text-[0.98rem] leading-8 text-soft/62">{copy.intro}</p>
        </section>

        <section className="mt-7 rounded-[2.5rem] border border-[#dce8ee] bg-white/80 p-6 shadow-[0_12px_28px_rgba(88,124,140,0.05)] sm:p-9">
          <h2 className="font-display text-2xl text-[#1f2b31]">{copy.verified}</h2>
          <dl className="mt-7 grid gap-4 md:grid-cols-2">
            {facts.map(([key, value]) => (
              <div key={key} className="rounded-[1.6rem] border border-[#e2edf2] bg-[#f8fbfc] p-5">
                <dt className="text-[0.62rem] uppercase tracking-[0.28em] text-soft/44">{copy.labels[key]}</dt>
                <dd className="mt-3 text-[0.94rem] leading-7 text-[#27363d]">{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-7 grid gap-7 lg:grid-cols-2">
          <section className="rounded-[2.5rem] border border-[#dce8ee] bg-white/80 p-7 sm:p-9">
            <h2 className="font-display text-2xl text-[#1f2b31]">{copy.evidence}</h2>
            <p className="mt-5 text-[0.9rem] leading-8 text-soft/60">{copy.evidenceBody}</p>
            <p className="mt-6 text-[0.68rem] uppercase tracking-[0.22em] text-soft/42">{copy.updated}</p>
          </section>
          <section className="rounded-[2.5rem] border border-[#dce8ee] bg-white/80 p-7 sm:p-9">
            <h2 className="font-display text-2xl text-[#1f2b31]">{copy.boundaries}</h2>
            <ul className="mt-5 space-y-4">
              {copy.boundaryItems.map((item) => <li key={item} className="flex gap-3 text-[0.9rem] leading-7 text-soft/60"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-aurora" />{item}</li>)}
            </ul>
          </section>
        </div>
      </div>
    </main>
  );
}
